import { accountAccessShowLogin } from 'actions/UiActions'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { ImageParams } from 'components/Common/ResponsiveImage'
import WalledContent from 'components/Common/WalledContent'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import LinePricetagAltIcon from 'LuxKit/Icons/line/LinePricetagAltIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import SoldOutMessage from 'components/OfferList/OfferListTiles/Support/SoldOutMessage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import OfferTilePropertyHeading from '../../../OfferList/OfferListTiles/SearchOfferTiles/TileSections/OfferTilePropertyHeading'
import { EmptyObject } from 'lib/object/objectUtils'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { isHotel } from 'lib/offer/offerTypes'

const imageParams: ImageParams = {
  mobileAspectRatio: '17:6',
  mobileWidth: '100vw',
  tabletAspectRatio: '2:1',
  tabletWidth: '400px',
  fit: 'center',
}

const Root = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: ${rem(140)} 1fr;
  height: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(120)} 1fr;
    grid-template-rows: auto;
    background-color: ${props => props.theme.palette.neutral.default.eight};
    box-shadow: ${props => props.theme.shadow.flat.large};
    border-radius: ${(props) => props.theme.borderRadius.S};
    min-width: ${rem(375)};

  }
`

const Container = styled(Group)`
  padding: ${rem(12)};
`

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
`

const ImageCarouselContainer = styled.div`
  position: relative;
`

interface Props {
  onClose?: () => void
  offer: App.Offer | App.OfferSummary
  checkInDate?: moment.Moment
  filters?: App.OfferListFilters
  offerUrl: string
  bestPricePackage?: App.Package
  bestPriceForDates?: App.OfferAvailableRate
  fetchingPrice: boolean
}

function LeSearchMapCard(props: Props) {
  const {
    onClose,
    offer,
    checkInDate,
    filters = EmptyObject,
    offerUrl,
    bestPricePackage,
    bestPriceForDates,
    fetchingPrice,
  } = props
  const dispatch = useAppDispatch()
  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const { images, type, property } = offer
  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)

  // We only show the first image
  const displayImages = useMemo(() => images.length && !isTabletScreenSizeAndUp ? [images[0]] : images, [images, isTabletScreenSizeAndUp])

  const loginHandler = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    dispatch(accountAccessShowLogin())
  }, [dispatch])

  const handleClose = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    onClose?.()
  }, [onClose])

  const handleSelectDatesClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (onAnyHotelSelectDatesPrompt && isHotel(offer)) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])

  const hidePrice = useHideAnytimePrices(offer.type, offerMetaData?.hidePricing)

  return (
    <Root>
      <ImageCarouselContainer>
        <ImageCarousel
          arrowBehaviour="hidden"
          images={displayImages}
          imageParams={imageParams}
          eagerLoadFirstImage
          bottomLeft={
            <CSSBreakpoint max="mobile" as={LabelGroup}>
              {type === 'hotel' && <ProductTypeLabel kind="default" productType="hotel" />}
              <OfferPartnershipLabel offer={offer} />
            </CSSBreakpoint>
          }
          topRight={
            <CSSBreakpoint max="mobile">
              <BookmarkButton offer={offer} />
            </CSSBreakpoint>
          }
        />
        {onClose && (
          <StyledIconButton
            size="small"
            shape="circle"
            kind="primary"
            onClick={handleClose}
            variant="ghost"
          >
            <LineTimesIcon />
          </StyledIconButton>
        )}
      </ImageCarouselContainer>
      <Container direction="vertical" gap={hidePrice ? 16 : 8}>
        <Group direction="horizontal" horizontalAlign="space-between" gap={4} verticalAlign="start">
          <Group direction="vertical" gap={4}>
            {property && <OfferTilePropertyHeading name={property.name} />}
            <BodyText variant="medium" colour="neutral-two" lineClamp={2}>{offer.name}</BodyText>
            <CSSBreakpoint min="tablet">
              {type === 'hotel' && <ProductTypeLabel productType={offer.productType} kind="plain" />}
            </CSSBreakpoint>
          </Group>
          {isOfferRatingDisplayable(property?.rating) && <OfferRating variant="xs" rating={property.rating} onlyNumber />}
        </Group>
        <ProductPaletteProvider product={offer}>
          <WalledContent
            enforced={offer.walledGarden}
            wall={
              <>
                <CaptionTextBlock
                  variant="medium"
                  weight="bold"
                  startIcon={<LinePricetagAltIcon />}
                >
                  Members get exclusive rates
                </CaptionTextBlock>
                <Group gap={8} direction="horizontal">
                  <TextButton
                    kind="secondary"
                    onClick={loginHandler}
                    size="small"
                  >
                    Log In
                  </TextButton>
                  <TextButton kind="primary" onClick={loginHandler} size="small">
                    Sign up for free
                  </TextButton>
                </Group>
              </>
            }
          >
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
              {!!bestPricePackage && !fetchingPrice &&
                <>
                  <OfferTilePricing
                    offer={offer}
                    hasDates={!!checkInDate}
                    checkInDate={checkInDate}
                    pkg={bestPricePackage}
                    offerUrl={offerUrl}
                    align="start"
                    availableRate={bestPriceForDates}
                    filters={filters}
                    showCompact
                    hideIncludesTaxesAndFees
                    hidePrice={hidePrice}
                  />
                  {!hidePrice && <CSSBreakpoint max="mobile">
                    <TextButton kind="primary">View offer</TextButton>
                  </CSSBreakpoint>}
                </>}
              {!(bestPriceForDates || bestPricePackage) && !fetchingPrice &&
                <div>
                  <SoldOutMessage offer={offer} filters={filters} />
                </div>}
            </Group>
            {hidePrice &&
              <TextButton kind="primary" fit="full-width" onClick={handleSelectDatesClick}>
                Select dates
              </TextButton>}
          </WalledContent>
        </ProductPaletteProvider>
      </Container>
    </Root>
  )
}

export default LeSearchMapCard
